import { Info } from 'lucide-react';
import React, { useMemo } from 'react';

import { SectionBreakCurve } from '@/components';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { ProfilesResponse } from '@/features/user';
import { useIsSmallScreen } from '@/lib/hooks/use-media-query';

import { confirmBatchModalContent } from '../../content/confirm-batch-modal';

import clockAndBooks from './assets/clock-and-books.webp';
import monitor from './assets/monitor.png';

export interface ConfirmBatchModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
  selectedProfile?: ProfilesResponse.Profile;
  doesSelectionIncludesWaitlist?: boolean;
  selectedWaitlistReason?: string;
  totalBatchCount?: number;
  voucherLabel?: string;
}

const ConfirmBatchModal = React.forwardRef<
  HTMLDivElement,
  ConfirmBatchModalProps
>(
  (
    {
      isOpen = false,
      onClose,
      onConfirm,
      isLoading = false,
      children,
      doesSelectionIncludesWaitlist = false,
      selectedWaitlistReason = '',
      totalBatchCount = 0,
      voucherLabel = 'US$50',
      ...props
    },
    ref
  ) => {
    const childProps = React.useMemo(() => {
      if (!children) return [];

      const flattenChildren = (node: React.ReactNode): React.ReactElement[] => {
        const result: React.ReactElement[] = [];
        React.Children.forEach(node, child => {
          if (React.isValidElement(child)) {
            if (child.type === React.Fragment) {
              const fragmentProps = child.props as {
                children?: React.ReactNode;
              };
              result.push(...flattenChildren(fragmentProps.children));
            } else {
              result.push(child);
            }
          }
        });
        return result;
      };

      return flattenChildren(children).map(
        child => child.props as Record<string, unknown>
      );
    }, [children]);

    const allChildrenAreWaitlist =
      childProps.length > 0 &&
      childProps.every(props => props.theme === 'waitlist');

    const ongoingChild = childProps.find(props => props.isOngoing === true);
    const isSmallScreen = useIsSmallScreen();
    const content = confirmBatchModalContent;

    const scenario = useMemo(() => {
      if (ongoingChild) {
        const batchName = (ongoingChild.batchCode as string) ?? '';
        const sessions = (ongoingChild.completedSessions as number) ?? 0;
        const isMixed = totalBatchCount > 1;
        const ongoing = isMixed
          ? content.ongoing.mixedOngoing(batchName, sessions)
          : content.ongoing.singleOngoing(batchName, sessions);
        return { type: 'ongoing' as const, ...ongoing };
      }
      const defaults =
        totalBatchCount > 1 ? content.default.multiple : content.default.single;
      return { type: 'default' as const, ...defaults };
    }, [ongoingChild, totalBatchCount, content]);

    const modalContent = (
      <>
        <div
          className={`${allChildrenAreWaitlist ? 'bg-hot-sun-yellow-light-cardbg' : 'bg-blue-chaos-light-cardbg'} p-1 flex justify-center items-center`}
        >
          <img
            src={allChildrenAreWaitlist ? clockAndBooks : monitor}
            alt={allChildrenAreWaitlist ? 'clock and books' : 'monitor'}
            className="w-35 h-35 object-contain"
          />
        </div>
        <SectionBreakCurve
          topColor={
            allChildrenAreWaitlist
              ? 'var(--color-hot-sun-yellow-light-cardbg)'
              : 'var(--color-blue-chaos-light-cardbg)'
          }
          bottomColor="var(--color-magical-moonlight-white)"
        />
        <div className="p-6 pt-1 flex flex-col gap-1 items-center">
          <h3
            className={`font-bold text-magical-moonlight-dark text-xl leading-7 text-center ${scenario.type === 'default' ? 'capitalize' : ''}`}
          >
            {scenario.heading}
          </h3>
          {scenario.type === 'ongoing' ? (
            <>
              <p className="font-bold text-sm text-center mt-1 text-blue-chaos-dark">
                {content.voucherSubheading(voucherLabel)}
              </p>
              <p className="font-medium text-sm text-magical-moonlight-d1 text-center leading-normal mt-1">
                {scenario.description}
              </p>
            </>
          ) : (
            <p className="font-medium text-muted-foreground text-sm leading-6 text-center">
              {scenario.subheading}
            </p>
          )}
          <div className="flex flex-col my-1">{children}</div>
          <div className="flex gap-1 items-center w-full mt-3">
            <div className="flex-1 mx-2 md:mx-6">
              <Button
                variant="outline"
                size={isSmallScreen ? 'sm' : 'lg'}
                fullWidth
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <div className="flex-1 mx-2 md:mx-6">
              <Button
                variant="contained"
                size={isSmallScreen ? 'sm' : 'lg'}
                fullWidth
                onClick={onConfirm}
                disabled={
                  isLoading ||
                  (doesSelectionIncludesWaitlist
                    ? selectedWaitlistReason === ''
                    : false)
                }
              >
                {isLoading ? 'Confirming...' : 'Yes, Confirm'}
              </Button>
            </div>
          </div>
          {scenario.type === 'ongoing' ? (
            <div className="flex items-start gap-1.5 mt-5 px-2">
              <Info className="w-4 h-4 text-magical-moonlight-d1 shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-magical-moonlight-d1 text-center leading-normal">
                {content.voucherFooter(voucherLabel)}{' '}
                <a
                  href={content.tncUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-bhanzu-orange underline font-semibold"
                >
                  {content.tncLabel}
                </a>
              </p>
            </div>
          ) : null}
        </div>
      </>
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={isSmallScreen ? 'sm' : 'xl'}
        className="bg-magical-moonlight-white rounded-3xl m-auto max-w-[calc(100%-1rem)]"
        ref={ref}
        {...props}
      >
        {modalContent}
      </Modal>
    );
  }
);

ConfirmBatchModal.displayName = 'ConfirmBatchModal';

export { ConfirmBatchModal };
export default ConfirmBatchModal;
